import { NOT_ENTERED_YET } from "@tfg/types/endpoints/agec"
import { useCallback } from "react"

import type { BusinessPartnerRepositoryAddressSnapshot } from "~/pages/Admin/components/form.types"
import { isNonEmptyTrimmedString } from "~/utils/isNonEmptyTrimmedString"

const snapshotAddressLineHasValue = (value: string | null): boolean => {
  if (value === null) {
    return false
  }
  if (!isNonEmptyTrimmedString(value)) {
    return false
  }
  return value.trim() !== NOT_ENTERED_YET
}

const BP_REPOSITORY_ADDRESS_FORM_FIELD_NAMES = [
  "organization.street",
  "organization.city",
  "organization.postalCode",
  "organization.country",
] as const

type BpRepositoryAddressFormFieldName =
  (typeof BP_REPOSITORY_ADDRESS_FORM_FIELD_NAMES)[number]

const BP_REPOSITORY_ADDRESS_FORM_FIELD_NAME_SET = new Set<string>(
  BP_REPOSITORY_ADDRESS_FORM_FIELD_NAMES
)

const isBusinessPartnerRepositoryAddressFormField = (
  fieldName: string
): fieldName is BpRepositoryAddressFormFieldName => {
  return BP_REPOSITORY_ADDRESS_FORM_FIELD_NAME_SET.has(fieldName)
}

const snapshotHasValueByField: Record<
  BpRepositoryAddressFormFieldName,
  (snapshot: BusinessPartnerRepositoryAddressSnapshot) => boolean
> = {
  "organization.street": (snapshot) =>
    snapshotAddressLineHasValue(snapshot.street),
  "organization.city": (snapshot) => snapshotAddressLineHasValue(snapshot.city),
  "organization.postalCode": (snapshot) =>
    snapshotAddressLineHasValue(snapshot.postalCode),
  "organization.country": (snapshot) =>
    snapshot.country !== null && snapshot.country !== undefined,
}

const repositoryRecordSnapshotHasValueForFormField = (
  fieldName: BpRepositoryAddressFormFieldName,
  snapshot: BusinessPartnerRepositoryAddressSnapshot
): boolean => {
  return snapshotHasValueByField[fieldName](snapshot)
}

type IsOrganizationFieldLockedForBpRepositoryParams = {
  readonly isUpdate: boolean
  readonly repositoryRecord: BusinessPartnerRepositoryAddressSnapshot | null
}

/**
 * Field lock policy when linking a BP repository company: registration id fully locked;
 * address fields locked only when the directory snapshot already has a value.
 */
export const useIsOrganizationFieldLockedForBpRepository = ({
  isUpdate,
  repositoryRecord,
}: IsOrganizationFieldLockedForBpRepositoryParams): ((fieldName: string) => boolean) => {
  return useCallback(
    (fieldName: string): boolean => {
      if (isUpdate) {
        return true
      }
      if (!isBusinessPartnerRepositoryAddressFormField(fieldName)) {
        return false
      }
      if (repositoryRecord === null) {
        return false
      }
      return repositoryRecordSnapshotHasValueForFormField(
        fieldName,
        repositoryRecord
      )
    },
    [isUpdate, repositoryRecord]
  )
}
